import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUsersStore } from '@/stores/users.store'
import { useRestaurantsStore } from '@/stores/restaurants.store'
import { getNamespaceSocket } from './useSocket'
import axiosIns from '@/plugins/axios'

const socket = ref(null)

// Retry state for the /agent namespace (see scheduleAgentReconnect).
let reconnectTimer = null
let reconnectAttempts = 0
const connected = ref(false)
const isOpen = ref(false)
const hasBeenOpened = ref(false)
const messages = ref([])
const streamingContent = ref('')
const isTyping = ref(false)
// Stop was requested and we're waiting for the server to confirm the abort —
// the Stop button shows "stopping" state and won't double-fire.
const stopRequested = ref(false)
const conversationId = ref(null)

/**
 * The member asked for a blank chat and has not used it yet.
 *
 * Kapil, 2026-09-22: "keep the resume unless the member specifically taps on
 * the icon we have to start a new session, then ALWAYS start a new session
 * even if within 12 hours." So this outranks the auto-resume window, and it is
 * mirrored to localStorage — module state alone would lose it on a reload and
 * put them back in the old conversation, which is the bug this fixes.
 */
const FRESH_CHAT_KEY = 'agentFreshChat'

function readFreshChat() {
  try {
    return localStorage.getItem(FRESH_CHAT_KEY) === '1'
  } catch {
    return false
  }
}

const memberStartedFresh = ref(readFreshChat())

function setMemberStartedFresh(value) {
  memberStartedFresh.value = value
  try {
    if (value) localStorage.setItem(FRESH_CHAT_KEY, '1')
    else localStorage.removeItem(FRESH_CHAT_KEY)
  } catch {
    // A blocked store only costs the reload case; the ref still holds.
  }
}
const conversations = ref([])
const currentToolCall = ref(null)
const error = ref(null)
const pendingReservationDialogId = ref(null)
const pendingReservationDialogStep = ref('8a')
const pendingReservationRestaurantId = ref(null)
const memberProfiles = ref({})
const restaurantCards = ref({})
const confirmedRestaurant = ref(null)
const confirmedBookingMember = ref(null)
const reservationSummary = ref(null)
// RM-253 — id of the draft saved the moment the member picked a restaurant.
// Drives the summary bar's "In Bookings" marker; null until a draft exists.
const reservationDraftId = ref(null)

// RM-398 — the open conversation is a group text thread this member is on.
// It is read here, never written in: { people, onOwnPhone, onBehalfBy,
// openNumbers } from the server (lib/agent/groupThreadView.js); null for the
// member's own chats.
const groupThread = ref(null)

// Admin agent tab support
const activeAgentType = ref('member')

// Access gating state
const accessStatus = ref(null) // { access, reason, betaAcknowledged }
const accessLoading = ref(false)

// Conversation list loading state
const conversationsLoading = ref(false)

// On (re)connect, resume the member's most recent conversation if it was active
// within this window; otherwise start fresh. Keeps the same session going so a
// member can pick up an in-progress request (e.g. resubmit after a decline)
// without losing context. An explicit "New Chat" always overrides this.
const AUTO_RESUME_WINDOW_MS = 12 * 60 * 60 * 1000
let autoResumeOnConnect = false
// RM-359: the chat a link asked for (/ai?chat=<id>, RM's follow-up on an unfinished request), opened
// instead of the usual resume the next time RM AI decides which conversation to show.
let linkedConversationId = null
// ...and from then until the server answers the load: on a first visit the socket can connect twice
// in quick succession (seen locally, 10/27), and a request in flight across that reconnect never
// gets its answer, so a reconnect asks again.
let linkedLoadPending = null

/**
 * Agent chat composable — manages socket.io connection, state, and actions.
 * Singleton state shared across all component instances.
 */
export function useAgentChat() {
  const usersStore = useUsersStore()
  const { myUser } = storeToRefs(usersStore)
  const isAdmin = computed(() => myUser.value?.userLevel === 'admin')
  const restaurantsStore = useRestaurantsStore()

  // Seed the friends-favorited store map from chat card payloads so the
  // restaurant details dialog (RestaurantProfile → FriendsLikedChips, which
  // reads the map by restaurantId) shows network favorites when opened from a
  // chat card/row — the map is otherwise only filled by the Discover flow.
  function seedFriendsFavorited(restaurants) {
    for (const r of restaurants || []) {
      if (r?._id && Array.isArray(r.friendsWhoFavorited) && r.friendsWhoFavorited.length) {
        restaurantsStore.friendsFavoritedByMap[r._id] = r.friendsWhoFavorited
      }
    }
  }

  /**
   * Check agent access status via REST before attempting socket connection.
   */
  async function checkAccess() {
    if (accessLoading.value) return accessStatus.value
    accessLoading.value = true
    try {
      const { data } = await axiosIns.get('/agent/access')
      accessStatus.value = data
      
      return data
    } catch {
      accessStatus.value = { access: false, reason: 'error', betaAcknowledged: false }
      
      return accessStatus.value
    } finally {
      accessLoading.value = false
    }
  }

  /**
   * Acknowledge the beta disclaimer.
   */
  async function acknowledgeBeta() {
    try {
      await axiosIns.post('/agent/acknowledge-beta')
      if (accessStatus.value) {
        accessStatus.value = { ...accessStatus.value, betaAcknowledged: true }
      }
    } catch (err) {
      console.error('Failed to acknowledge beta', err)
    }
  }

  function connect() {
    // If we already hold the cached namespace socket, don't rebuild it — but if
    // it has dropped (server restart, or navigating away from /ai and back), it
    // must be reconnected so the 'connect' handler fires again and any pending
    // resume (resumeOrStartConversation) actually runs. Previously this returned
    // early whenever socket.value existed, so a dropped socket never reconnected
    // and the chat came back blank instead of resuming the recent conversation.
    if (socket.value) {
      if (!socket.value.connected) socket.value.connect()

      return
    }

    const s = getNamespaceSocket('/agent')
    if (!s) return

    function commitStreamingMessage() {
      const content = streamingContent.value
      if (!content.trim()) return

      messages.value.push({
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
      })
      streamingContent.value = ''
    }

    // Remove any stale listeners from a previous connect/disconnect cycle
    // (the namespace socket is cached and reused by getNamespaceSocket)
    s.removeAllListeners()

    s.on('connect', () => {
      connected.value = true
      error.value = null
      clearAgentReconnect()
      if (autoResumeOnConnect) {
        autoResumeOnConnect = false
        performResumeOrStart()

        return
      }

      // RM-261 — this is a RE-connect after a drop (phone locked, app
      // backgrounded, wifi handoff). The server no longer throws away an
      // in-flight turn when the socket dies, so the reply we were waiting for
      // very likely completed and persisted while we were away. Without this
      // refetch the member comes back to their own message hanging with
      // nothing after it — which is exactly the experience the server-side fix
      // set out to end, so leaving the client alone would only half-deliver it.
      //
      // The streamed-so-far state belongs to a socket that no longer exists,
      // so clear it UNCONDITIONALLY — including for a brand-new chat whose
      // first turn died before `conversation_created` arrived, where there is
      // no id to reload but the UI is just as stuck. Kapil hit this on prod:
      // the typing dots kept animating and Stop could not be pressed, because
      // isProcessing (isTyping || streamingContent || currentToolCall) stays
      // true with no turn behind it, and Stop is disabled while `!connected`.
      isTyping.value = false
      streamingContent.value = ''
      currentToolCall.value = null
      stopRequested.value = false

      // RM-359: a linked chat asked for before this reconnect and never answered: ask again.
      if (linkedLoadPending && !conversationId.value) {
        linkedConversationId = linkedLoadPending
        linkedLoadPending = null
        performResumeOrStart()

        return
      }

      // With an open conversation, reload it: the server (RM-261) no longer
      // discards an in-flight turn, so the reply likely completed while we
      // were away and is waiting to be shown.
      if (conversationId.value) {
        loadConversation(conversationId.value)
      }
    })

    s.on('disconnect', () => {
      connected.value = false

      // The Manager retries the transport on its own; this covers a namespace
      // that does not come back with it.
      scheduleAgentReconnect()
    })

    /**
     * The TRANSPORT retries for ever (useSocket's Manager: reconnection true,
     * no attempt cap), so a dropped connection normally heals itself. A
     * NAMESPACE connect error did not: it recorded the message and stopped, so
     * RM AI sat on four disabled launcher tiles until the member reloaded,
     * with nothing saying why (Kapil hit this on 2026-09-22 after the dev API
     * restarted under an open page; a deploy can do the same in production).
     */
    s.on('connect_error', err => {
      connected.value = false
      error.value = err.message
      scheduleAgentReconnect()
    })

    s.on('conversation_created', ({ conversationId: id }) => {
      conversationId.value = id
    })

    s.on('agent_typing', () => {
      isTyping.value = true
      streamingContent.value = ''
    })

    s.on('agent_chunk', ({ chunk }) => {
      streamingContent.value += chunk
      isTyping.value = false
    })

    s.on('agent_tool_start', ({ toolName, toolCallId, displayName }) => {
      // Persist any streamed preamble before the model pauses to call a tool.
      commitStreamingMessage()
      currentToolCall.value = { toolName, toolCallId, displayName }
    })

    s.on('agent_tool_end', ({ toolName, success, changed, restaurantId } = {}) => {
      currentToolCall.value = null
      if (!success) return

      // The AI just wrote server-side state the app caches. The server says WHICH caches
      // (`changed`), so an open page refreshes itself instead of the member reloading —
      // Founder review, 2026-09-17: a note saved in chat did not appear on the restaurant
      // page, and the heart stayed empty, until the page was closed and reopened.
      const touched = Array.isArray(changed) ? changed : []

      // Older servers sent no `changed`; keep the one case that already worked.
      if (!touched.length && toolName === 'update_my_profile') touched.push('user')

      if (touched.includes('user')) usersStore.getMyUser().catch(() => {})
      if (touched.includes('notes')) {
        restaurantsStore.refreshFavoriteNotes().catch(() => {})
        restaurantsStore.refreshUnfavoritedNotes().catch(() => {})
      }
      if (touched.includes('favorites')) {
        restaurantsStore.applyFavoriteLocally(restaurantId, toolName !== 'unfavorite_restaurant')
      }
      if (touched.includes('restaurants')) restaurantsStore.getRestaurants().catch(() => {})
    })

    s.on('agent_done', ({ content }) => {
      // Commit the active stream for normal single-round responses.
      commitStreamingMessage()
      if (content) {
        const lastMessage = messages.value[messages.value.length - 1]
        if (lastMessage?.role !== 'assistant' || lastMessage.content !== content) {
          messages.value.push({
            role: 'assistant',
            content,
            timestamp: new Date().toISOString(),
          })
        }
      }
      streamingContent.value = ''
      isTyping.value = false
      currentToolCall.value = null
      stopRequested.value = false
    })

    // The member stopped the turn: the server aborted the OpenAI stream and
    // persisted whatever partial reply existed. Mirror agent_done, but mark
    // the reply so it renders with its "stopped" caption.
    s.on('agent_generation_stopped', ({ content }) => {
      commitStreamingMessage()
      const lastMessage = messages.value[messages.value.length - 1]
      if (content && (lastMessage?.role !== 'assistant' || lastMessage.content !== content)) {
        messages.value.push({
          role: 'assistant',
          content,
          timestamp: new Date().toISOString(),
        })
      }
      const committed = messages.value[messages.value.length - 1]
      if (committed?.role === 'assistant') {
        messages.value[messages.value.length - 1] = { ...committed, stopped: true }
      }
      streamingContent.value = ''
      isTyping.value = false
      currentToolCall.value = null
      stopRequested.value = false
    })

    s.on('agent_error', ({ error: errMsg }) => {
      commitStreamingMessage()
      messages.value.push({
        role: 'error',
        content: errMsg,
        timestamp: new Date().toISOString(),
      })
      streamingContent.value = ''
      isTyping.value = false
      currentToolCall.value = null
      stopRequested.value = false
    })

    s.on('open_reservation_dialog', ({ reservationId, openAtStep, confirmedRestaurantId }) => {
      pendingReservationDialogStep.value = openAtStep || '8a'
      pendingReservationDialogId.value = reservationId
      pendingReservationRestaurantId.value = confirmedRestaurantId || null
    })

    s.on('agent_member_profile', ({ userId, profile }) => {
      if (userId && profile) {
        memberProfiles.value = { ...memberProfiles.value, [userId]: profile }
      }
    })

    s.on('agent_restaurants', ({ restaurants }) => {
      if (!Array.isArray(restaurants)) return
      const next = { ...restaurantCards.value }
      for (const r of restaurants) {
        if (r?.profileId) next[r.profileId] = r
        if (r?._id) next[r._id] = r
      }
      restaurantCards.value = next
      seedFriendsFavorited(restaurants)
    })

    s.on('agent_restaurant_confirmed', ({ restaurant }) => {
      confirmedRestaurant.value = restaurant || null
    })

    s.on('agent_booking_member_confirmed', ({ member }) => {
      confirmedBookingMember.value = member || null
    })

    s.on('agent_reservation_summary', ({ summary, reservationDraftId: draftId }) => {
      reservationSummary.value = summary || null
      reservationDraftId.value = draftId || null
    })

    s.on('conversation_title_updated', ({ conversationId: id, title }) => {
      // Update in conversation list if present
      const conv = conversations.value.find(c => c._id === id)
      if (conv) conv.title = title
    })

    // RM-205: a turn arrived over another channel (email/SMS) — if it's the
    // conversation on screen, reload it so the new messages appear live.
    s.on('external_channel_turn', ({ conversationId: id }) => {
      if (id && id === conversationId.value && !isTyping.value && !streamingContent.value) {
        loadConversation(id)
      }
    })

    // After turn is saved, update the pending user message with its DB index
    s.on('agent_turn_saved', ({ userMsgDbIndex, hasTrace, assistantMsgDbIndex, displayMessages }) => {
      // Find the last user message without a msgDbIndex assigned
      for (let i = messages.value.length - 1; i >= 0; i--) {
        if (messages.value[i].role === 'user' && messages.value[i].msgDbIndex == null) {
          messages.value[i] = {
            ...messages.value[i],
            msgDbIndex: userMsgDbIndex,
            hasTrace: !!hasTrace,
          }
          break
        }
      }
      if (Array.isArray(displayMessages)) {
        // Reconcile the live view with what the server persisted for this
        // turn: every pre-tool bubble committed while streaming is replaced
        // by the server's display list, which drops interim replies the
        // model wrote before its tools ran (gpt-4.1 answer-before-tools —
        // previously those bubbles stayed on screen until a reload). Also
        // anchors each surviving reply to its db index for 👍/👎.
        let turnStart = messages.value.length
        for (let i = messages.value.length - 1; i >= 0; i--) {
          if (messages.value[i].role === 'user') {
            turnStart = i + 1
            break
          }
        }
        const tail = messages.value.slice(turnStart).filter(m => m.role !== 'assistant')
        messages.value = [
          ...messages.value.slice(0, turnStart),
          ...displayMessages.map(dm => ({
            role: 'assistant',
            content: dm.content,
            timestamp: new Date().toISOString(),
            msgDbIndex: dm.msgDbIndex ?? null,
            hasTrace: false,
            ...(dm.stopped ? { stopped: true } : {}),
          })),
          ...tail,
        ]
      } else if (assistantMsgDbIndex != null) {
        // Older server payload without displayMessages: anchor the
        // just-streamed AI reply to its persisted db index so the member
        // 👍/👎 affordance works without a reload.
        for (let i = messages.value.length - 1; i >= 0; i--) {
          if (messages.value[i].role === 'assistant' && messages.value[i].msgDbIndex == null) {
            messages.value[i] = { ...messages.value[i], msgDbIndex: assistantMsgDbIndex }
            break
          }
        }
      }
    })

    socket.value = s
    s.connect()

    // If the namespace socket was already connected, the 'connect' event won't
    // re-fire — run any deferred resume decision now.
    if (s.connected && autoResumeOnConnect) {
      autoResumeOnConnect = false
      performResumeOrStart()
    }
  }

  /**
   * Retry the /agent namespace with a capped backoff while the chat is open.
   * Cleared as soon as a connect succeeds.
   */
  function scheduleAgentReconnect() {
    if (!isOpen.value || reconnectTimer) return
    reconnectAttempts += 1
    const delay = Math.min(1000 * (2 ** (reconnectAttempts - 1)), 15000)

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      if (!isOpen.value || socket.value?.connected) return

      // disconnect() nulls the ref, and a namespace can be torn down under us
      // — rebuild rather than retrying a socket that is no longer there.
      if (socket.value) socket.value.connect()
      else connect()
    }, delay)
  }

  function clearAgentReconnect() {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    reconnectTimer = null
    reconnectAttempts = 0
  }

  function disconnect() {
    clearAgentReconnect()
    if (socket.value) {
      socket.value.disconnect()
      socket.value = null
    }
    connected.value = false
  }

  // meta.inputMethod (RM-163): 'typed' | 'voice_transcription' — analytics
  // only; the server whitelists the value and it never affects AI behavior.
  // meta.attachedImageId: imageId of a file the member attached (already uploaded
  // via POST /media) — the server ownership-validates it and confirms the type.
  // meta.attachedFileType: 'image' | 'pdf', so the live bubble renders the right thing.
  function sendMessage(text, meta = {}) {
    const trimmed = (text || '').trim()
    const attachedImageId = meta.attachedImageId || null
    const attachedFileType = attachedImageId ? (meta.attachedFileType || 'image') : null
    if (!socket.value?.connected || (!trimmed && !attachedImageId)) return
    // A group text thread continues on the phone, not here (RM-398).
    if (groupThread.value) return

    // The blank chat is about to become a real one.
    setMemberStartedFresh(false)

    // Add user message immediately (msgDbIndex will be set after agent_turn_saved)
    // A tapped chip is not rendered as a bubble — the chip fills on the reply
    // above (AgentChat skips `chip` messages) and the answer follows directly.
    const chip = meta.inputMethod === 'chip'
    // A tapped launcher tile (any of the four) is persisted as the member's turn but never shown as a bubble:
    // the member lands straight on the server-posted opener (Kapil, 2026-09-16).
    const hidden = meta.inputMethod === 'starter'
    messages.value.push({
      role: 'user',
      content: trimmed,
      attachedImageId,
      attachedFileType,
      chip,
      hidden,
      timestamp: new Date().toISOString(),
      msgDbIndex: null,
      hasTrace: false,
    })

    socket.value.emit('send_message', {
      conversationId: conversationId.value,
      message: trimmed,
      agentType: activeAgentType.value,
      inputMethod: meta.inputMethod === 'voice_transcription' ? 'voice_transcription' : (chip ? 'chip' : (hidden ? 'starter' : 'typed')),
      attachedImageId,
    }, response => {
      if (response?.conversationId) {
        conversationId.value = response.conversationId
      }
    })
  }

  // Abort the in-flight turn server-side. The server confirms with
  // agent_generation_stopped (or agent_done if the turn won the race).
  function stopGeneration() {
    if (!socket.value?.connected || !conversationId.value || stopRequested.value) return
    stopRequested.value = true
    socket.value.emit('stop_generation', { conversationId: conversationId.value }, response => {
      // Nothing was in flight (turn already finished) — clear the pending state.
      if (!response?.stopping) stopRequested.value = false
    })
  }

  // Re-run the last member message as a fresh turn. The repeated member
  // message renders as a "Regenerated" divider, not a duplicate bubble.
  function regenerate() {
    if (!socket.value?.connected || !conversationId.value || isProcessing.value) return

    const lastUser = [...messages.value].reverse().find(m => m.role === 'user')
    if (!lastUser) return

    messages.value.push({
      role: 'user',
      content: lastUser.content,
      attachedImageId: lastUser.attachedImageId || null,
      attachedFileType: lastUser.attachedFileType || null,
      timestamp: new Date().toISOString(),
      msgDbIndex: null,
      hasTrace: false,
      regenerated: true,
    })

    socket.value.emit('regenerate', {
      conversationId: conversationId.value,
      agentType: activeAgentType.value,
    })
  }

  function listConversations() {
    if (!socket.value?.connected) return

    conversationsLoading.value = true
    socket.value.emit('list_conversations', { agentType: activeAgentType.value }, response => {
      if (response?.conversations) {
        conversations.value = response.conversations
      }
      conversationsLoading.value = false
    })
  }

  /**
   * `auto` marks the resume that happens when RM AI opens, as opposed to the
   * member picking a conversation from their history. Only the automatic one
   * declines to land on a starter-only conversation — choosing one from the
   * list is their decision and is always honoured.
   */
  function loadConversation(id, { auto = false, onFail = null } = {}) {
    if (!socket.value?.connected) return

    // Choosing one from history is a decision to leave the blank chat.
    if (!auto) setMemberStartedFresh(false)

    socket.value.emit('load_conversation', { conversationId: id }, response => {
      if (linkedLoadPending === id)
        linkedLoadPending = null
      if (response?.error) {
        if (onFail)
          onFail()
        else
          error.value = response.error

        return
      }
      conversationId.value = response.conversationId
      messages.value = (response.messages || []).map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        msgDbIndex: m.msgDbIndex ?? null,
        hasTrace: m.hasTrace ?? false,
        stopped: m.stopped ?? false,
        regenerated: m.regenerated ?? false,
        chip: m.chip ?? false,
        hidden: m.hidden ?? false,
        // The server sends these on a reloaded conversation; dropping them
        // made a photo or PDF the member had attached disappear when they came
        // back to the chat, so their own message read as if they had sent
        // nothing (RM-301 hand test).
        attachedImageId: m.attachedImageId ?? null,
        attachedFileType: m.attachedFileType ?? null,
        // Where the turn happened ('sms', 'email'; absent = the app). Dropping
        // it hid the "via text" / "via email" label on a reloaded conversation
        // (Kapil, 10/2).
        channel: m.channel ?? null,
        // RM-205 member support: a person at RM wrote this reply.
        supportReply: m.supportReply ?? false,
        // RM-398: in a group text thread, who wrote it (someone else on the thread).
        senderName: m.senderName ?? '',
        fromOther: m.fromOther ?? false,
      }))
      groupThread.value = response.readOnly && response.group ? response.group : null
      // Nothing but a tile tap and its opener: land on the tiles instead.
      if (auto && isStarterOnly(messages.value)) {
        startNewConversation()

        return
      }
      memberProfiles.value = response.memberProfiles || {}
      confirmedRestaurant.value = response.confirmedRestaurant || null
      confirmedBookingMember.value = response.confirmedBookingMember || null
      reservationSummary.value = response.reservationSummary || null
      reservationDraftId.value = response.reservationDraftId || null
      // RM-261 — the turn we were waiting for may still be running (we
      // reconnected mid-generation). Show the typing state and wait: the reply
      // arrives on the conversation room when it completes, instead of the
      // chat looking finished with the answer missing.
      isTyping.value = !!response.turnInProgress

      seedFriendsFavorited(response.restaurantCards)
      const nextRestaurantCards = {}
      for (const restaurant of response.restaurantCards || []) {
        if (restaurant?.profileId) nextRestaurantCards[restaurant.profileId] = restaurant
        if (restaurant?._id) nextRestaurantCards[restaurant._id] = restaurant
      }
      restaurantCards.value = nextRestaurantCards
    })
  }

  /**
   * `explicit` means the MEMBER asked for a new chat (New Chat in the
   * history menu), as opposed to the component clearing stale state on mount.
   *
   * An empty conversation does not exist server-side until the first message,
   * so auto-resume would otherwise find the previous one and pull them
   * straight back out of the blank chat they just asked for (Kapil,
   * 2026-09-22). The flag is only ever SET here — never cleared — so
   * navigating away and back keeps them in their new chat; sending a message
   * or opening a conversation from history clears it.
   */
  function startNewConversation({ explicit = false } = {}) {
    if (explicit) setMemberStartedFresh(true)
    conversationId.value = null
    messages.value = []
    streamingContent.value = ''
    currentToolCall.value = null
    error.value = null
    memberProfiles.value = {}
    restaurantCards.value = {}
    confirmedRestaurant.value = null
    confirmedBookingMember.value = null
    reservationSummary.value = null
    reservationDraftId.value = null
    groupThread.value = null
  }

  // Decide, on chat open, whether to resume the most recent conversation or
  // start fresh. Resumes only when the last conversation was active within
  // AUTO_RESUME_WINDOW_MS; otherwise starts new. Requires a live socket, so if
  // we aren't connected yet it defers until the next 'connect'.
  /**
   * The four launcher tiles, by the exact text they send. Kept in step with
   * lib/agent/starters.js (which keys on these strings) and AgentChat.vue's
   * launcherSuggestions.
   */
  const STARTER_PROMPTS = new Set([
    'Request a reservation',
    'Check availability',
    'Discover',
    'Add to Profile',
  ])

  /**
   * A conversation that is ONLY a tile tap and the fixed opener the server
   * posted back — the member never said anything of their own.
   *
   * Resuming one of those reopens RM AI on a stale "Add to Profile" and hides
   * the four tiles, which reads as though the app decided for them (Kapil,
   * 2026-09-22). It is theirs to reopen from history; it is not what RM AI
   * should land on.
   */
  function isStarterOnly(list) {
    if (!Array.isArray(list) || list.length !== 2) return false
    const [first, second] = list

    return first?.role === 'user'
      && STARTER_PROMPTS.has(String(first.content || '').trim())
      && second?.role === 'assistant'
  }

  /**
   * RM-359: open this chat next time RM AI decides which conversation to show — a link
   * (/ai?chat=<id>) back into the chat a request was started in. The server opens only the
   * member's own; a chat it won't open falls back to the usual resume.
   */
  function openLinkedConversation(id) {
    linkedConversationId = id || null
  }

  function performResumeOrStart() {
    if (!socket.value?.connected) {
      autoResumeOnConnect = true

      return
    }
    autoResumeOnConnect = false

    const linked = linkedConversationId

    linkedConversationId = null

    // They asked for a blank chat and have not used it yet. Leave them in it — unless a link
    // asked for one chat.
    if (memberStartedFresh.value && !linked) return

    // Pending until the server answers the load (or turns it down): a reconnect asks again.
    if (linked)
      linkedLoadPending = linked

    // The member's chats first, then the one to open: a load sent the moment the socket
    // connects can go unanswered on a first visit (RM-359, found testing the link), and this is
    // the order the resume has always used.
    socket.value.emit('list_conversations', { agentType: activeAgentType.value }, response => {
      const list = response?.conversations || []
      // Their own most recent chat: a group text thread is only ever opened
      // by choosing it from the list (RM-398).
      const mostRecent = list.find(c => c?._id && !c.group)
      const recentEnough = mostRecent?.lastActivityAt
        && (Date.now() - new Date(mostRecent.lastActivityAt).getTime()) < AUTO_RESUME_WINDOW_MS
      const resumeMostRecent = () => {
        if (recentEnough)
          loadConversation(mostRecent._id, { auto: true })
        else
          startNewConversation()
      }

      // RM-359: a link into one chat opens it; one the server won't open (not theirs, or gone)
      // falls back to the usual resume.
      if (linked)
        loadConversation(linked, { onFail: resumeMostRecent })
      else
        resumeMostRecent()
    })
  }

  function resumeOrStartConversation() {
    if (socket.value?.connected) {
      performResumeOrStart()
    } else {
      autoResumeOnConnect = true
    }
  }

  function switchAgentType(type) {
    if (type === activeAgentType.value) return
    activeAgentType.value = type

    // Clear current conversation state when switching
    startNewConversation()
    conversations.value = []
  }

  async function toggleChat() {
    if (!isOpen.value) {
      hasBeenOpened.value = true

      // Check access before opening
      if (!accessStatus.value) {
        await checkAccess()
      }

      // Only connect socket if access is granted
      if (accessStatus.value?.access && !socket.value?.connected) {
        connect()
      }
    }
    isOpen.value = !isOpen.value
  }

  function closeChat() {
    isOpen.value = false
  }

  async function fetchTrace(convId, msgDbIndex) {
    const { data } = await axiosIns.get(
      `/agent/conversations/${convId}/trace`,
      { params: { msgIndex: msgDbIndex } },
    )

    return data.traceData
  }

  const isProcessing = computed(() =>
    isTyping.value || !!streamingContent.value || !!currentToolCall.value,
  )

  function clearPendingReservationDialog() {
    pendingReservationDialogId.value = null
    pendingReservationDialogStep.value = '8a'
    pendingReservationRestaurantId.value = null
  }

  return {
    // State
    connected,
    isOpen,
    hasBeenOpened,
    messages,
    streamingContent,
    isTyping,
    stopRequested,
    conversationId,
    conversations,
    conversationsLoading,
    currentToolCall,
    error,
    isProcessing,
    isAdmin,
    pendingReservationDialogId,
    pendingReservationDialogStep,
    pendingReservationRestaurantId,
    memberProfiles,
    restaurantCards,
    activeAgentType,
    accessStatus,
    accessLoading,
    confirmedRestaurant,
    confirmedBookingMember,
    reservationSummary,
    reservationDraftId,
    groupThread,

    // Actions
    connect,
    disconnect,
    sendMessage,
    stopGeneration,
    regenerate,
    listConversations,
    loadConversation,
    startNewConversation,
    resumeOrStartConversation,
    openLinkedConversation,
    switchAgentType,
    toggleChat,
    closeChat,
    fetchTrace,
    clearPendingReservationDialog,
    checkAccess,
    acknowledgeBeta,
  }
}
